import { useMemo } from "react";
import { Select } from "antd";
import type { SelectProps } from "antd";
import { useList } from "@refinedev/core";
import { useTranslation } from "react-i18next";

interface VarietyRow {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

interface Props
  extends Omit<SelectProps<number | undefined>, "options" | "loading"> {
  /** Si true, también muestra variedades archivadas (is_active=false). */
  includeInactive?: boolean;
}

/**
 * Selector reutilizable para `catalogue.Variety` (las "Variedades
 * Protegidas" del catálogo nuevo). Carga la lista desde
 * `/api/varieties/` y devuelve el `id` numérico. Por defecto sólo
 * activas; se le puede pasar `includeInactive` para pantallas admin
 * que quieren ver también las archivadas (caso típico: edición de
 * una parcela que aún apunta a una variedad descontinuada).
 *
 * Acepta todas las props de antd `Select` (placeholder, allowClear,
 * disabled, value, onChange...), así se inserta tal cual en un
 * `Form.Item`.
 */
export const VarietySelect = ({
  includeInactive = false,
  showSearch = true,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const { result, query } = useList<VarietyRow>({
    resource: "varieties",
    pagination: { mode: "off" },
    filters: includeInactive
      ? [{ field: "include_inactive", operator: "eq", value: 1 }]
      : [],
    queryOptions: {
      staleTime: 5 * 60 * 1000, // 5 min — el catálogo cambia poco.
    },
  });
  const isLoading = query.isLoading;

  const options = useMemo(() => {
    const rows = result?.data ?? [];
    return rows.map((v: VarietyRow) => ({
      value: v.id,
      label: v.is_active ? v.name : `${v.name} (archivada)`,
      // Datos extra para el filtro de búsqueda.
      _searchKey: `${v.name} ${v.code}`.toLowerCase(),
    }));
  }, [result]);

  return (
    <Select
      placeholder={t("varieties.select_placeholder", "Selecciona variedad")}
      loading={isLoading}
      options={options}
      showSearch={showSearch}
      optionFilterProp="label"
      filterOption={(input, option) =>
        (option as any)?._searchKey?.includes(input.toLowerCase()) ?? false
      }
      style={{ minWidth: 200, ...rest.style }}
      {...rest}
    />
  );
};

export default VarietySelect;
